import { createJSONStorage } from 'zustand/middleware';
import { createSuperJSONStorage } from '../createSuperJSONStorage';
import { createTenantStorage } from '../tenantStorage';
import {
  migrateLayoutPersistedState,
  LAYOUT_PERSIST_VERSION,
} from './stores/layout.migration';
import {
  migrateTabScopedPersistedState,
  TAB_SCOPED_PERSIST_VERSION,
} from './stores/tab-scoped.migration';
import { recentItemsMigration } from './stores/recent-items.migration';
import { targetVisitsMigration } from './stores/target-visits.migration';
import { createPersistMigrator } from './persist-migration';

export const PERSIST_STORE_KEYS = {
  layout: 'terra-proxy-layout-state',
  targetVisits: 'terra-proxy-target-visits',
  tabScoped: 'terra-proxy-tab-scoped-store',
  recentItems: 'terra-proxy-recent-items',
} as const;
export type PersistStoreKey =
  (typeof PERSIST_STORE_KEYS)[keyof typeof PERSIST_STORE_KEYS];
type PersistMigrationRegistration = {
  version: number;
  migrate: (persistedState: unknown, version: number) => unknown;
};
const PERSIST_MIGRATIONS: Record<
  PersistStoreKey,
  PersistMigrationRegistration
> = {
  [PERSIST_STORE_KEYS.layout]: {
    version: LAYOUT_PERSIST_VERSION,
    migrate: migrateLayoutPersistedState,
  },
  [PERSIST_STORE_KEYS.targetVisits]: {
    version: targetVisitsMigration.version,
    migrate: createPersistMigrator(targetVisitsMigration),
  },
  [PERSIST_STORE_KEYS.tabScoped]: {
    version: TAB_SCOPED_PERSIST_VERSION,
    migrate: migrateTabScopedPersistedState,
  },
  [PERSIST_STORE_KEYS.recentItems]: {
    version: recentItemsMigration.version,
    migrate: createPersistMigrator(recentItemsMigration),
  },
};

type TenantPersistStorage = 'json' | 'superjson';

export function createTenantPersistConfig({
  storeKey,
  storage = 'json',
}: {
  storeKey: PersistStoreKey;
  storage?: TenantPersistStorage;
}) {
  const registration = PERSIST_MIGRATIONS[storeKey];

  return {
    name: storeKey,
    version: registration.version,
    migrate: registration.migrate,
    storage:
      storage === 'superjson'
        ? createSuperJSONStorage(() => createTenantStorage())
        : createJSONStorage(() => createTenantStorage()),
  };
}
